import { useEffect, useState } from "react";

import { useParams } from "@tanstack/react-router";

import { apiClient } from "@/api/apiClient";
import { getAssessmentByCaseNumberApiV1AssessmentCaseCaseNumberGet } from "@/api/generated/sdk.gen";
import { Button } from "@/components/ui/button";

type FilteredAssessment = {
  full_transcript: string | null;
  full_summary: string | null;
  history_of_injury: string | null;
  history_of_treatment: string | null;
  history_of_progress_over_time: string | null;
  other_relevant_history: string | null;
  educational_occupational_background: string | null;
  activities_of_daily_living: string | null;
  medication: string | null;
  physical_examination: string | null;
  mental_state_examination: string | null;
  current_injury_status: string | null;
  past_medical_history: string | null;
  one_line_summary: string | null;
  notes: string | null;
};

const ResultJsonPage = () => {
  const { id: caseNumber } = useParams({ strict: false }) as { id: string };
  const [jsonData, setJsonData] = useState<
    FilteredAssessment | { error: string } | null
  >(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchAndDisplayJson = async () => {
      if (!caseNumber) {
        setJsonData({ error: "Case number is required" });
        return;
      }

      try {
        const response =
          await getAssessmentByCaseNumberApiV1AssessmentCaseCaseNumberGet({
            client: apiClient,
            path: { case_number: caseNumber },
          });

        if ("error" in response && response.error) {
          console.error("Failed to fetch assessment:", response.error);
          setJsonData({ error: "Failed to fetch assessment" });
          return;
        }

        if (!response.data) {
          setJsonData({ error: "Assessment not found" });
          return;
        }

        const assessment = response.data;

        // Filter to only include the specified fields, extracting content from AssessmentFieldStatusSchema
        const filteredAssessment: FilteredAssessment = {
          full_transcript: assessment.full_transcript?.content ?? null,
          full_summary: assessment.full_summary?.content ?? null,
          history_of_injury: assessment.history_of_injury?.content ?? null,
          history_of_treatment: assessment.history_of_treatment?.content ?? null,
          history_of_progress_over_time:
            assessment.history_of_progress_over_time?.content ?? null,
          other_relevant_history: assessment.other_relevant_history?.content ?? null,
          educational_occupational_background:
            assessment.educational_occupational_background?.content ?? null,
          activities_of_daily_living:
            assessment.activities_of_daily_living?.content ?? null,
          medication: assessment.medication?.content ?? null,
          physical_examination: assessment.physical_examination?.content ?? null,
          mental_state_examination:
            assessment.mental_state_examination?.content ?? null,
          current_injury_status: assessment.current_injury_status?.content ?? null,
          past_medical_history: assessment.past_medical_history?.content ?? null,
          one_line_summary: assessment.one_line_summary?.content ?? null,
          notes: assessment.notes ?? null,
        };

        setJsonData(filteredAssessment);
      } catch (error) {
        console.error("Error fetching assessment:", error);
        setJsonData({ error: "Failed to fetch assessment" });
      }
    };

    fetchAndDisplayJson();
  }, [caseNumber]);

  const handleCopy = async () => {
    if (jsonData === null) return;

    const jsonString = JSON.stringify(jsonData, null, 2);
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  if (jsonData === null) {
    return (
      <div style={{ padding: "20px" }}>
        <pre>Loading...</pre>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <div
        style={{
          marginBottom: "12px",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button onClick={handleCopy} variant="outline" size="sm">
          {copied ? "Copied!" : "Copy JSON"}
        </Button>
      </div>
      <pre
        style={{
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          padding: "16px",
          backgroundColor: "#f5f5f5",
          borderRadius: "4px",
          border: "1px solid #e0e0e0",
          fontSize: "14px",
          lineHeight: "1.5",
          overflow: "auto",
          maxHeight: "calc(100vh - 120px)",
          userSelect: "text",
        }}
      >
        {JSON.stringify(jsonData, null, 2)}
      </pre>
    </div>
  );
};

export default ResultJsonPage;
